import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useAppServices } from "../hooks/AppContext.tsx";
import { useGeneration } from "../hooks/bindings.ts";
import { saveLabel } from "../view-models/labels.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import { TypeTag } from "../components/TypeTag.tsx";
import styles from "../design-system/ui.module.css";

export function ReviewMeaningsScreen() {
  const state = useGeneration();
  const { generation } = useAppServices();
  const navigate = useNavigate();
  const ready = state.draft?.items.filter((item) => item.status === "ready" && item.pack) ?? [];
  const reviewIndex = state.draft?.reviewIndex ?? 0;
  const current = ready[reviewIndex];

  useEffect(() => {
    if (state.phase === "saved" && state.savedWordId) {
      const path =
        (state.savedMeaningIds?.length ?? 0) === 1 && state.savedMeaningIds
          ? `/meanings/${state.savedMeaningIds[0]}`
          : `/words/${state.savedWordId}`;
      navigate(path, { replace: true });
    }
    if (state.phase === "idle") {
      navigate("/add", { replace: true });
    }
  }, [state, navigate]);

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar title="Review New Meanings" backTo="/add/choose" />
        {state.phase === "generating" ? (
          <div className={styles.empty}>{state.progressMessage ?? "Creating learning cards…"}</div>
        ) : null}
        {state.lookingForAnother ? (
          <button className={styles.textButton} type="button" onClick={() => navigate("/add/choose")}>
            Looking for another meaning?
          </button>
        ) : null}
        {state.error ? <p className={styles.error}>{state.error.message}</p> : null}
        {current?.pack && state.draft ? (
          <>
            <div className={styles.reviewMeter}>
              <span>
                Meaning {reviewIndex + 1} of {ready.length}
              </span>
              <div className={styles.reviewDots}>
                {ready.map((item, index) => (
                  <button
                    key={item.itemId}
                    type="button"
                    className={`${styles.reviewDot} ${index === reviewIndex ? styles.reviewDotActive : ""}`}
                    aria-label={`Preview meaning ${index + 1}`}
                    onClick={() => generation.setReviewIndex(index)}
                  />
                ))}
              </div>
            </div>
            <h1 className={styles.heroWord}>
              {state.draft.displayText} <TypeTag type={current.pack.type} />
            </h1>
            <p className={styles.meaningTitle}>{current.pack.shortLabel}</p>
            {current.reviewNote ? <div className={styles.banner}>{current.reviewNote}</div> : null}
            <section className={styles.detail}>
              <h2>Meaning</h2>
              <p>{current.pack.definition}</p>
            </section>
            {current.pack.russianHint ? (
              <section className={styles.detail}>
                <h2>Russian hint</h2>
                <p>{current.pack.russianHint}</p>
              </section>
            ) : null}
            <section className={styles.detail}>
              <h2>Example</h2>
              <p>{current.pack.example}</p>
            </section>
            {current.pack.collocations.length ? (
              <section className={styles.detail}>
                <h2>Common patterns</h2>
                <div className={styles.tags}>
                  {current.pack.collocations.map((item) => (
                    <span className={styles.tag} key={item}>
                      {item}
                    </span>
                  ))}
                </div>
              </section>
            ) : null}
            {current.pack.memoryKey ? (
              <section className={`${styles.detail} ${styles.memory}`}>
                <h2>Memory key</h2>
                <p>{current.pack.memoryKey}</p>
              </section>
            ) : null}
            <div className={styles.stack} style={{ gridTemplateColumns: "1fr 1fr", marginTop: 14 }}>
              <PrimaryButton secondary disabled={reviewIndex === 0} onClick={() => generation.setReviewIndex(reviewIndex - 1)}>
                Previous
              </PrimaryButton>
              <PrimaryButton
                secondary
                disabled={reviewIndex >= ready.length - 1}
                onClick={() => generation.setReviewIndex(reviewIndex + 1)}
              >
                Next
              </PrimaryButton>
            </div>
            <button className={styles.textButton} type="button" onClick={() => generation.removeItem(current.itemId)}>
              Remove this meaning from the batch
            </button>
            {state.draft.items.some((item) => item.status === "failed") ? (
              <div className={styles.banner}>
                <div>
                  Some meanings failed. Repair them or remove them before saving.
                  {state.draft.items
                    .filter((item) => item.status === "failed")
                    .map((item) => (
                      <div key={item.itemId}>
                        {item.candidate.shortLabel}: {item.error?.message}{" "}
                        <button className={styles.textButton} type="button" onClick={() => void generation.retryItem(item.itemId)}>
                          Retry
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            ) : null}
            <div className={styles.footerAction}>
              <PrimaryButton disabled={state.phase === "saving" || ready.length === 0} onClick={() => void generation.save()}>
                {saveLabel(ready.length)}
              </PrimaryButton>
              <p className={styles.sub} style={{ textAlign: "center", marginTop: 9 }}>
                Nothing is saved until you confirm.
              </p>
            </div>
          </>
        ) : state.phase !== "generating" ? (
          <div className={styles.empty}>No complete cards to review yet.</div>
        ) : null}
      </Screen>
    </Frame>
  );
}
